import {
  ClockCircleOutlined,
  FolderOutlined,
  PlusOutlined,
  StarFilled,
  StarOutlined,
  TeamOutlined,
  UsergroupAddOutlined
} from "@ant-design/icons"
import { Button, Flex, Skeleton, Tooltip, Typography } from "antd"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { ActionTooltip } from "../../../../components/ActionTooltip.tsx"
import { SearchBar } from "../../../../components/Input/SearchBar.tsx"
import { brand, neutral, semantic } from "../../../../components/widgets/tokens.ts"
import { groupReportsByFolder } from "../../helpers/reportFolders.ts"
import { ReportIconGlyph } from "../../helpers/reportIcons.tsx"
import { reachesBeyondItsShares } from "../../helpers/reportVisibility.ts"
import type { ReportListItem } from "../../types/types.ts"

export type ReportsNav = "all" | "favorites" | "recent" | "shared"

const NAV_ITEMS: {
  key: ReportsNav
  labelKey: string
  icon: React.ReactNode
}[] = [
  { key: "all", labelKey: "reports.rail.nav.all", icon: <FolderOutlined /> },
  {
    key: "favorites",
    labelKey: "reports.rail.nav.favorites",
    icon: <StarOutlined />
  },
  {
    key: "recent",
    labelKey: "reports.rail.nav.recent",
    icon: <ClockCircleOutlined />
  },
  { key: "shared", labelKey: "reports.rail.nav.shared", icon: <TeamOutlined /> }
]

const { Text } = Typography

const isSharedWithMe = (report: ReportListItem): boolean =>
  (report.access_level ?? "owner") !== "owner"

interface ReportsRailProps {
  reports: ReportListItem[]
  loading: boolean
  activeNav: ReportsNav
  selectedId?: string
  search: string
  onNavChange: (nav: ReportsNav) => void
  onSearch: (value: string | null) => void
  onSelect: (report: ReportListItem) => void
  onCreate: () => void
  onToggleFavorite: (report: ReportListItem) => void
}

export const ReportsRail = ({
  reports,
  loading,
  activeNav,
  selectedId,
  search,
  onNavChange,
  onSearch,
  onSelect,
  onCreate,
  onToggleFavorite
}: ReportsRailProps) => {
  const { t } = useTranslation()

  const groups = useMemo(() => groupReportsByFolder(reports), [reports])

  const renderRow = (report: ReportListItem) => {
    const selected = report.id === selectedId
    const shared = isSharedWithMe(report) || reachesBeyondItsShares(report.visibility)
    return (
      <div
        key={report.id}
        role="button"
        tabIndex={0}
        aria-current={selected}
        onClick={() => onSelect(report)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            onSelect(report)
          }
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "7px 8px",
          borderRadius: 8,
          cursor: "pointer",
          background: selected ? "rgba(42,125,140,.10)" : "transparent"
        }}
      >
        <ReportIconGlyph
          iconKey="bar_chart"
          style={{
            color: selected ? brand.teal : neutral.muted,
            fontSize: 15
          }}
        />
        <Text
          ellipsis={{ tooltip: report.name }}
          style={{
            flex: 1,
            minWidth: 0,
            fontWeight: selected ? 600 : 500,
            color: selected ? brand.teal : neutral.ink
          }}
        >
          {report.name}
        </Text>
        {shared && (
          <Tooltip title={t("reports.rail.sharedIndicator")}>
            <UsergroupAddOutlined
              style={{ color: neutral.faint, fontSize: 13 }}
            />
          </Tooltip>
        )}
        <ActionTooltip
          title={
            report.is_favorite
              ? t("reports.actions.unfavorite")
              : t("reports.actions.favorite")
          }
        >
          <Button
            type="text"
            size="small"
            aria-label={
              report.is_favorite
                ? t("reports.actions.unfavorite")
                : t("reports.actions.favorite")
            }
            onClick={(e) => {
              e.stopPropagation()
              onToggleFavorite(report)
            }}
            icon={
              report.is_favorite ? (
                <StarFilled style={{ color: semantic.star }} />
              ) : (
                <StarOutlined style={{ color: neutral.faint }} />
              )
            }
          />
        </ActionTooltip>
      </div>
    )
  }

  return (
    <Flex
      vertical
      gap="middle"
      style={{
        width: 264,
        flex: "none",
        minHeight: 0,
        borderRight: `1px solid ${neutral.border}`,
        paddingRight: 16
      }}
    >
      {/* SearchBar defaults to flex:1 for horizontal toolbars; in this vertical
          column that would stretch it over all free rail height. */}
      <SearchBar
        placeholder={t("reports.searchPlaceholder")}
        defaultValue={search}
        onSearch={onSearch}
        style={{ flex: "none" }}
      />

      <Flex vertical gap={2}>
        {NAV_ITEMS.map((item) => {
          const active = item.key === activeNav
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => onNavChange(item.key)}
              aria-pressed={active}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                width: "100%",
                padding: "8px 12px",
                borderRadius: 8,
                border: "none",
                cursor: "pointer",
                textAlign: "left",
                fontWeight: active ? 600 : 500,
                color: active ? brand.teal : neutral.ink,
                background: active ? "rgba(42,125,140,.10)" : "transparent"
              }}
            >
              {item.icon}
              <span>{t(item.labelKey)}</span>
            </button>
          )
        })}
      </Flex>

      <Flex align="center" justify="space-between">
        <Text
          type="secondary"
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.05em",
            textTransform: "uppercase"
          }}
        >
          {t("reports.rail.savedReports")}
        </Text>
        <ActionTooltip title={t("reports.rail.addReport")}>
          <Button
            type="text"
            size="small"
            icon={<PlusOutlined />}
            aria-label={t("reports.rail.addReport")}
            onClick={onCreate}
          />
        </ActionTooltip>
      </Flex>

      <div
        data-testid="rail-saved-reports"
        style={{ flex: 1, minHeight: 0, overflowY: "auto" }}
      >
        {loading && reports.length === 0 ? (
          <Skeleton active paragraph={{ rows: 4 }} title={false} />
        ) : groups.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 13 }}>
            {t("reports.rail.empty")}
          </Text>
        ) : (
          <Flex vertical gap={4}>
            {groups.map((group) => (
              <Flex vertical gap={2} key={group.folder || "__ungrouped__"}>
                {group.folder && (
                  <Text
                    type="secondary"
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      padding: "4px 8px 0"
                    }}
                  >
                    {group.folder}
                  </Text>
                )}
                {group.reports.map(renderRow)}
              </Flex>
            ))}
          </Flex>
        )}
      </div>
    </Flex>
  )
}
